import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RcFile } from 'antd/lib/upload/interface';

import { Button, Modal, Title } from '@gerdau/hefesto';
import { UploadFile } from '@gerdau/hefesto-mais-web';

import {
  Container,
  ContentButton,
  Header,
  Message,
  MessageWrapperChooseItem,
  Wrapper
} from './styles';
import SpreadsheetConfigModal from './SpreadsheetConfigModal';

import { ApplicationState } from '../../../../../store';
import {
  addSearchedItem as addSearchItemAction,
  cleanSpreadSheetMVP,
  removeSearchedItem as removeSearchedItemAction,
  spreadSheetMVP
} from '../../../../../store/ducks/long-steel-order-item-search/actions';
import {
  ECodeType,
  ESpreadsheetField,
  EUserPreferenceType,
  IOrderEntryPreference,
  ISaveUser
} from '../../../../../store/ducks/user/types';

import { ICustomer } from '../../../../../store/ducks/customers/types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import MessageChooseItem from '../../../../../components/MessageChooseItem';
import SearchedListMvp from '../SearchedListMvp';
import { EOrderSettingsRoutes } from '../orderSettings.routes';
import { ILongSteelOrderItem } from '../../../../../store/ducks/long-steel-shopping-cart/types';
import LoadingItems from '../LoadingItems';
import {
  saveUser,
  updateUserPreferences
} from '../../../../../store/ducks/user/actions';
import { ShoppingCartBottomBar } from '../../../../../components/Purchase/BottomBar';
import { ILongSteelOrderItemSearchState } from '../../../../../store/ducks/long-steel-order-item-search/types';
import { CartBulkEntry, NewCartItem } from '../../../../../services/MVP/models';
import { useCartService } from '../../../../../services/MVP/useCartService';
import {
  MultProductsResponse,
  useProductForCustomer
} from '../../../../../services/MVP/useProductForCustomer';
import { IProductCardTagging } from '../../../../../store/ducks/tagging/types';
import { cleanShoppingCart } from '../../../../../store/ducks/long-steel-shopping-cart/actions';
import { BlockedModal } from '../../../../../components/Purchase/BlockedModal';
import useOnRouteChange from '../../../../../hooks/useOnRouteChange';
import moment from 'moment/moment';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { ProductNotFoundChip } from '../SpreadsheetTableMvp/styles';

const orderEntryPreferenceDefault: IOrderEntryPreference = {
  name: EUserPreferenceType.ORDER_ENTRY,
  codeType: ECodeType.SKU,
  spreadsheetLine: 3,
  spreadsheetColumns: [
    {
      key: 0,
      field: ESpreadsheetField.SKU,
      label: 'Código da Gerdau',
      position: 'A'
    },
    {
      key: 1,
      field: ESpreadsheetField.CLIENT_SKU,
      label: 'Meu código interno',
      position: ''
    },
    {
      key: 2,
      field: ESpreadsheetField.QUANTITY,
      label: 'Quantidade',
      position: 'B'
    },
    { key: 3, field: ESpreadsheetField.UNITY, label: 'Unidade', position: 'C' },
    {
      key: 4,
      field: ESpreadsheetField.DESIRED_DATE,
      label: 'Data desejada',
      position: 'D'
    },
    {
      key: 5,
      field: ESpreadsheetField.CUSTOMER_PURCHASE_ITEM,
      label: 'Item do pedido',
      position: 'E'
    }
  ]
};

interface ISpreadsheetUploadMvpProps {
  emitterId: string;
}

const SpreadsheetUploadMvp: React.FC<ISpreadsheetUploadMvpProps> = ({
  emitterId
}) => {
  const cart = useCartService();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { getMultProducts } = useProductForCustomer();
  const [filename, setFilename] = useState<string>('');

  const { commerceScreenName } = getTaggingInformationScreen(
    document.location.pathname
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [magentoProducts, setMagentoProducts] = useState<
    MultProductsResponse[] | null
  >(null);
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(true);

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const orderItemSearch = useSelector(
    (state: ApplicationState) => state.longSteelOrderItemSearch
  );
  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity
  );

  const user = useSelector((state: ApplicationState) => state.user);
  const userLoading = useSelector(
    (state: ApplicationState) => state.user.loading
  );
  const userPreferences = useSelector(
    (state: ApplicationState) => state.user.preferences
  );

  const selectedCustomer = customersState.customers.find(
    (c) => c.customerId === emitterId
  );
  const isBlockedCustomer =
    selectedCustomer?.blocks &&
    selectedCustomer?.blocks?.filter((blc) => blc.type === 'order')?.length > 0;
  const [openBlockedModal, setOpenBlockedModal] = useState(isBlockedCustomer);
  const onCloseBlockedModal = () => {
    dispatch(cleanShoppingCart());
    setOpenBlockedModal(false);
  };

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const createSpreadsheetSubmissionTags = (
    type:
      | 'baixar_modelo_planilha'
      | 'configurar_planilha'
      | 'enviar_planilha'
      | 'upload_planilha'
  ) => {
    dispatch(
      sendTagging({
        screenName: 'enviar_planilha',
        etapa: 'enviar_planilha',
        event: 'envio_planilha',
        tipo_envio: `${type}`
      })
    );
  };

  useEffect(() => {
    setOpenBlockedModal(isBlockedCustomer);
  }, [selectedCustomer]);

  useOnRouteChange(() => {
    dispatch(cleanSpreadSheetMVP());
  });

  const handleTagging = (buttonLabel: string) => {
    dispatch(
      sendTagging({
        event: 'click',
        screenName: commerceScreenName,
        etapa: 'selecionar_emissor_recebedor',
        botao_navegacao: buttonLabel
      })
    );
  };

  const customerLoading =
    customersState.loading ||
    !!customersState.loadingPurchaseReceivers ||
    !!customersState.loadingProductDescription ||
    !!customersState.loadingBlocks ||
    !!customersState.loadingCustomerById ||
    !!userLoading ||
    !selectedCustomer?.productsDescriptionResponse;

  const isLoading = customerLoading || loading;

  const [canShowConfigModal, setCanShowConfigModal] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProductsNotFoundModal, setShowProductsNotFoundModal] =
    useState(false);

  const getUserOrderEntryPreference = (): IOrderEntryPreference => {
    const orderEntryIdx = userPreferences.findIndex(
      (preference) => preference.name === EUserPreferenceType.ORDER_ENTRY
    );

    useEffect(() => {
      canShowConfigModal && !isLoading
        ? createSpreadsheetSubmissionTags('configurar_planilha')
        : '';
    }, [canShowConfigModal]);
    if (
      orderEntryIdx !== -1 &&
      (userPreferences[orderEntryIdx] as IOrderEntryPreference)
        .spreadsheetColumns
    ) {
      const orderEntryPreferences = userPreferences[
        orderEntryIdx
      ] as IOrderEntryPreference;

      if (
        orderEntryPreferences.spreadsheetColumns.length <
        orderEntryPreferenceDefault.spreadsheetColumns.length
      ) {
        orderEntryPreferenceDefault.spreadsheetColumns.forEach((col) => {
          if (
            !orderEntryPreferences.spreadsheetColumns.find(
              (item) => item.field === col.field
            )
          ) {
            orderEntryPreferences.spreadsheetColumns.push({
              ...col,
              position: ''
            });
          }
        });
      }

      return orderEntryPreferences;
    } else {
      return orderEntryPreferenceDefault;
    }
  };

  const [userOrderEntryPreference, setUserOrderEntryPreference] =
    useState<IOrderEntryPreference>(getUserOrderEntryPreference());

  const updateUserOrderEntryPreference = (
    newUserOrderEntryPreference: IOrderEntryPreference
  ) => {
    const saveUserProps: ISaveUser = {
      user: user,
      userPreference: newUserOrderEntryPreference
    };
    dispatch(saveUser(saveUserProps));
    // dispatch(updateUserPreferences(newUserOrderEntryPreference));
    setUserOrderEntryPreference(newUserOrderEntryPreference);
    setCanShowConfigModal(false);
  };

  const handleRemoveSearchedItems = () => {
    dispatch(removeSearchedItemAction([]));
    setShowDeleteModal(false);
    const filteredPrices = filterPricesAndsearchedItems();
    dispatch(
      sendTagging({
        event: 'remove_from_cart',
        ecommerce: {
          screenName: 'sua_planilha',
          tipo_compra: 'planilha',
          paginacao: '1',
          items: searchedItems.map(
            (item) =>
              ({
                screenName: 'sua_planilha',
                tipo_compra: 'planilha',
                item_id: item.sku ?? '',
                item_name: item.description,
                price: filteredPrices.find(
                  (filteredProduct) => filteredProduct.sku === item.sku
                )?.price,
                unidade_de_medida: item.measureUnit,
                quantity: item.quantity?.toString() ?? 1,
                item_category: filteredPrices.find(
                  (filteredProduct) => filteredProduct.sku === item.sku
                )?.description,
                cod_gerdau: item.sku,
                preco_acordado: filteredPrices.find(
                  (filteredProduct) => filteredProduct.sku === item.sku
                )?.hasAgreementPrice
              } as IProductCardTagging)
          )
        }
      })
    );
  };

  const handleCancelModal = () => {
    setShowDeleteModal(false);
  };
  const handleTaggingProductNotFoundModal = (buttonLabel: string) => {
    dispatch(
      sendTagging({
        event: 'modal_produtos_nao_identificados',
        screenName: 'planilha',
        botao_navegacao: buttonLabel
      })
    );
  };

  const removeAllSearchedItem = () => {
    setShowDeleteModal(true);
  };

  const ModalRemoveAllSearchedItems = () => {
    if (!showDeleteModal) {
      return null;
    }

    return (
      <Modal
        buttons={{
          left: {
            children: 'Manter todos',
            name: 'cancel',
            onClick: handleCancelModal,
            type: 'button',
            variant: 'secondary'
          },
          right: {
            children: 'Excluir todos',
            name: 'delete-all',
            onClick: handleRemoveSearchedItems,
            type: 'button',
            variant: 'danger'
          }
        }}
        modalSize="sm"
        title="Excluir todos"
      >
        Tem certeza que deseja excluir todos os itens?
      </Modal>
    );
  };

  const ModalProductNotFound = () => {
    if (!showProductsNotFoundModal) {
      return null;
    }

    const closeModal = () => {
      setShowProductsNotFoundModal(false);
      handleTaggingProductNotFoundModal('Voltar');
    };
    dispatch(
      sendTagging({
        event: 'informacoes_nao_identificadas',
        screenName: 'planilha',
        mensagem: 'Deseja avançar para o carrinho mesmo assim?'
      })
    );
    return (
      <Modal
        buttons={{
          left: {
            children: 'Voltar',
            name: 'back',
            onClick: closeModal,
            type: 'button',
            variant: 'secondary'
          },
          right: {
            children: 'Avançar',
            name: 'next',
            onClick: onContinueToCheckout,
            type: 'button',
            variant: 'primary'
          }
        }}
        modalSize="sm"
        title="Sua planilha contém inconsistências nos dados"
      >
        Deseja avançar para o carrinho mesmo assim?
      </Modal>
    );
  };

  const addSearchedItem = (items: ILongSteelOrderItem[]) => {
    const updatedSearchedItems = orderItemSearch.searchedItems.map(
      (searchItem: ILongSteelOrderItem, index: number) => {
        const findSelectedItem = items.find(
          (addItem: ILongSteelOrderItem) => addItem.tableId === index
        );
        return findSelectedItem
          ? { ...searchItem, addedToCart: true }
          : searchItem;
      }
    );
    dispatch(addSearchItemAction([...updatedSearchedItems]));
  };

  const removeSearchedItem = (productIndex: number) => {
    const searchedItems = orderItemSearch.searchedItems;
    dispatch(
      removeSearchedItemAction([
        ...searchedItems.filter((item, index) => index !== productIndex)
      ])
    );
    const removedItem = searchedItems.find(
      (item, index) => index === productIndex
    );

    if (removedItem) {
      const matchingPrice =
        magentoProducts &&
        magentoProducts.find((product) => product.sku === removedItem.sku);
      const price = matchingPrice
        ? matchingPrice.price.isAgreementPrice === 'X'
          ? String(matchingPrice.price.salesAgreementData.salesAgreement)
          : matchingPrice.price.creditPrice
        : undefined;
      const filteredProducts = filterPricesAndsearchedItems();

      dispatch(
        sendTagging({
          event: 'remove_from_cart',
          ecommerce: {
            screenName: 'sua_planilha',
            tipo_compra: 'planilha',
            paginacao: '',
            items: [
              {
                screenName: 'sua_planilha',
                tipo_compra: 'planilha',
                item_id: removedItem.sku ?? '',
                item_name: removedItem.description,
                price: price,
                unidade_de_medida: removedItem.measureUnit,
                quantity: removedItem.quantity?.toString() ?? 1,
                item_category: filteredProducts.find(
                  (filteredProduct) => filteredProduct.sku === removedItem.sku
                )?.description,
                cod_gerdau: removedItem.sku,
                preco_acordado: filteredProducts.find(
                  (filteredProduct) => filteredProduct.sku === removedItem.sku
                )?.hasAgreementPrice
              }
            ]
          }
        })
      );
    }
  };

  const onConfirmSpreadsheetConfig = (
    orderEntryPreference: IOrderEntryPreference,
    hasConfigChanged: boolean
  ) => {
    if (hasConfigChanged) {
      updateUserOrderEntryPreference(orderEntryPreference);
      return;
    }
    setCanShowConfigModal(false);
  };

  const customerData: ICustomer = customersState.customers.find(
    (customer) => customer.customerId === emitterId
  ) as ICustomer;

  const onFileUploaded = (file: RcFile) => {
    if (userOrderEntryPreference) {
      setFilename(file.name);
      dispatch(
        spreadSheetMVP(
          emitterId,
          ECodeType.SKU,
          file,
          userOrderEntryPreference,
          customerData.division,
          customerData.locality.state || ''
        )
      );
    }
    dispatch(
      sendTagging({
        etapa: 'planilha_carregada',
        screenName: 'enviar_planilha',
        event: 'planilha_carregada'
      })
    );
  };

  const searchedItems = useSelector(
    (state: { longSteelOrderItemSearch: ILongSteelOrderItemSearchState }) =>
      state.longSteelOrderItemSearch.searchedItems
  );

  const { push } = useHistory();

  const convertSpreadsheetResponse = async (
    orderItems: ILongSteelOrderItem[]
  ): Promise<CartBulkEntry[]> => {
    const skuList = orderItems.map((i) => i.sku);
    let productsPrice: MultProductsResponse[];
    if (
      magentoProducts &&
      Array.isArray(magentoProducts) &&
      magentoProducts.every((mp) => skuList.includes(mp.sku))
    ) {
      productsPrice = magentoProducts;
    } else {
      productsPrice = await getMultProducts(
        skuList,
        ECodeType.SKU,
        emitterId,
        skuList.length,
        1,
        customerData.division
      );
    }
    if (productsPrice) {
      setMagentoProducts(productsPrice);
      return orderItems
        .filter((orderItem) => {
          const magProduct = productsPrice.find(
            (product) => product.sku === orderItem.sku
          );
          return (
            magProduct &&
            // confirmando que o preço existe e tem preço
            magProduct.price &&
            magProduct.price.creditPrice &&
            ((!isNaN(Number(magProduct.price.creditPrice)) &&
              Number(magProduct.price.creditPrice) > 0) ||
              (magProduct.price.isAgreementPrice === 'X' &&
                !isNaN(
                  Number(magProduct.price.salesAgreementData.salesAgreement)
                ) &&
                Number(magProduct.price.salesAgreementData.salesAgreement) > 0))
          );
        })
        .map((orderItem) => {
          const magProduct = productsPrice.find(
            (product) => product.sku === orderItem.sku
          );

          const entry: CartBulkEntry = {
            sku: orderItem.sku,
            qty:
              !orderItem.quantity || isNaN(orderItem.quantity)
                ? 1
                : orderItem.quantity,
            quote_id: cart.getCartId(),
            extension_attributes: {
              measure_unity_gerdau: '',
              destination_gerdau: '',
              payment_type_gerdau: '',
              sap_fields: {
                o_header_sap: [],
                o_item_sap: [],
                o_price_sap: []
              }
            }
          };

          const isPriceAgreement = magProduct?.price.isAgreementPrice === 'X';
          entry.extension_attributes.has_agreed_price_gerdau = isPriceAgreement;

          // measure_unity_gerdau
          const mu = orderItem.measureUnit.length
            ? orderItem.measureUnit
            : isPriceAgreement
            ? magProduct?.price.salesAgreementData.agreementUm
            : magProduct?.price.um;

          entry.extension_attributes.measure_unity_gerdau = mu ?? '';

          // expected_date_gerdau
          let minimumDate = '';
          if (orderItem.desiredDate.length) {
            minimumDate = new Date(orderItem.desiredDate)
              .toISOString()
              .replace('Z', '');
          }
          // else if (
          //   Array.isArray(orderItem.destinations) &&
          //   orderItem.destinations.length
          // ) {
          //   minimumDate = orderItem.destinations[0].minimumDate;
          // }
          const today = moment().startOf('day');
          const date = moment(minimumDate);
          if (
            !minimumDate ||
            minimumDate.length === 0 ||
            date.day() === 0 ||
            date.day() === 6 ||
            (minimumDate && date.isSameOrBefore(today))
          ) {
            minimumDate = '';
          }
          entry.extension_attributes.expected_date_gerdau = minimumDate;

          // destination
          entry.extension_attributes.destination_gerdau = isPriceAgreement
            ? magProduct?.price.salesAgreementData.destination ?? ''
            : magProduct?.price.destination ?? '';

          // payment_type_gerdau
          entry.extension_attributes.payment_type_gerdau = isPriceAgreement
            ? magProduct?.price.salesAgreementData.paymentTerms ?? 'Z021'
            : magProduct?.price.paymentTerms ?? 'Z021';

          return entry;
        });
    }
    return [];
  };

  const onContinueToCheckout = async () => {
    // handleTaggingProductNotFoundModal('Avançar');
    try {
      setShowProductsNotFoundModal(false);
      setLoading(true);
      if (isMounted.current) {
        setIsContinueDisabled(true);
      }
      // const cartQuantity = await getCartItemQuantity({ countType: true });
      const filterOnlyFoundProducts = searchedItems.filter(
        (item) => item.found
      );
      const entries = await convertSpreadsheetResponse(filterOnlyFoundProducts);
      if (!entries) {
        throw new Error('Erro ao tentar adicionar os itens no carrinho!');
      }
      const qtyToAddOnCart = 30 - Number(cartQuantity);
      if (qtyToAddOnCart > 0) {
        const bulkCartAdd = await cart.addBulkCartItem({
          items: entries.slice(0, qtyToAddOnCart) as NewCartItem[],
          withOptions: false,
          withPrice: false,
          noCheckPrice: true
        });
        if (bulkCartAdd && bulkCartAdd.length > 0) {
          createContinueToCheckoutTag();
          push(`./${EOrderSettingsRoutes.SHOPPING_CART}`);
        }
      } else {
        dispatch(
          sendTagging({
            event: 'error',
            screenName: commerceScreenName,
            mensagem: 'Seu carrinho atingiu o limite de 30 produtos.'
          })
        );
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Seu carrinho atingiu o limite de 30 produtos.',
            durationInMS: 3000,
            labelActionOne: 'X'
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setIsContinueDisabled(false);
      }
    }
  };

  const toType = (obj: any): string => {
    return (
      {}.toString
        .call(obj)
        .match(/\s([a-zA-Z]+)/)?.[1]
        .toLowerCase() || ''
    );
  };

  const itemUnavailable = (
    <ProductNotFoundChip>Indisponível</ProductNotFoundChip>
  );

  const measureUnitFormat = (measureUnit: string | ReactNode) => {
    const regex = /[a-zA-Z]/;
    if (typeof measureUnit === 'string' && regex.test(measureUnit)) {
      return measureUnit;
    }
    return itemUnavailable;
  };

  const dateFormat = (date: string | Date | ReactNode) => {
    const dateType = toType(date);
    if (['string', 'date'].includes(dateType)) {
      const today = moment().startOf('day');
      const value = moment(
        dateType === 'date' ? (date as Date) : (date as string)
      );
      if (value.isSameOrBefore(today)) {
        return itemUnavailable;
      }
      if (value.isValid()) {
        return value.format('DD/MM/YYYY');
      }
      return '-';
    }
    return date;
  };

  const hasInvalidMeasureUnitOrDate = (
    items: ILongSteelOrderItem[]
  ): boolean => {
    return items.some(
      (item) => item.measureUnit === '' || item.desiredDate === ''
    );
  };

  const hasUnavailableDateOrMeasureUnit = (items: any[]): boolean => {
    return items.some((item) => {
      const formattedDate = dateFormat(item.desiredDate);
      const formattedMeasureUnit = measureUnitFormat(item.measureUnit);
      return (
        (React.isValidElement(formattedDate) &&
          formattedDate.type === itemUnavailable.type) ||
        (React.isValidElement(formattedMeasureUnit) &&
          formattedMeasureUnit.type === itemUnavailable.type)
      );
    });
  };

  const goToNextStep = async (productsNotFound: boolean) => {
    handleTagging('avancar_para_carrinho');
    const hasInvalidData = hasInvalidMeasureUnitOrDate(searchedItems);
    const hasUnavailableData = hasUnavailableDateOrMeasureUnit(searchedItems);
    setTimeout(
      () =>
        setShowProductsNotFoundModal(
          productsNotFound || hasInvalidData || hasUnavailableData
        ),
      0
    );
    if (!productsNotFound && !hasInvalidData && !hasUnavailableData) {
      await onContinueToCheckout();
    }
  };

  const createContinueToCheckoutTag = () => {
    const filteredPrices = searchedItems.map((item) => {
      const findMatchingPrice = magentoProducts?.find(
        (priceItem) => priceItem.sku === item.sku
      );

      if (!findMatchingPrice) {
        return {
          ...item,
          price: ''
        };
      }

      const matchingPrice = findMatchingPrice as unknown as NewCartItem;

      if (!matchingPrice.price) {
        return {
          ...item,
          price: ''
        };
      }

      const price =
        matchingPrice.price.isAgreementPrice === 'X' &&
        matchingPrice.price.salesAgreementData.salesAgreement
          ? String(matchingPrice.price.salesAgreementData.salesAgreement)
          : matchingPrice.price.creditPrice;

      return {
        ...item,
        price
      };
    });
    const filteredProducts = filterPricesAndsearchedItems();
    dispatch(
      sendTagging({
        event: 'add_to_cart',
        ecommerce: {
          screenName: 'sua_planilha',
          tipo_compra: 'planilha',
          paginacao: '',
          items: filteredPrices.map((item) => ({
            screenName: 'sua_planilha',
            tipo_compra: 'planilha',
            item_id: item?.sku || '',
            item_name: item.description,
            price: item.price,
            unidade_de_medida: item.measureUnit,
            quantity: item.quantity?.toString() ?? 1,
            item_category: filteredProducts.find(
              (filteredProduct) => filteredProduct.sku === item.sku
            )?.description,
            cod_gerdau: item.sku,
            preco_acordado: true
          }))
        }
      })
    );
  };

  const createTagsForProductsNotFound = (
    notFoundItems: ILongSteelOrderItem[]
  ) => {
    const notFoundIndex: number[] = [];
    const taggingObject = notFoundItems.reduce<{ [key: string]: string }>(
      (acc, item, index) => {
        acc[`codigo_material${index + 1}`] = item.sku;
        notFoundIndex.push(index + 1);
        return acc;
      },
      {}
    );
    const noProductsFound =
      notFoundItems.length === orderItemSearch.searchedItems.length;
    const eventName = `${
      noProductsFound ? 'produto_nao_encontrado' : 'produtos_nao_identificados'
    }`;
    const eventMessage = `Produtos das linhas ${formatIndexes(
      notFoundIndex
    )} não ${noProductsFound ? 'encontrados' : 'identificados'}`;

    dispatch(
      sendTagging({
        etapa: 'enviar_planilha',
        screenName: 'enviar_planilha',
        event: eventName,
        event_category: 'erro',
        ...taggingObject,
        message: eventMessage
      })
    );
  };

  const formatIndexes = (notFoundIndex: number[]) => {
    return notFoundIndex.length > 1
      ? `${notFoundIndex.slice(0, -1).join(', ')} e ${notFoundIndex.slice(-1)}`
      : notFoundIndex[0];
  };

  const processPricesAndSendTag = async () => {
    try {
      setLoading(true);
      const filterOnlyFoundProducts = ({ found }: ILongSteelOrderItem) => found;
      const skuList = searchedItems
        .filter(filterOnlyFoundProducts)
        .map((i) => i.sku);

      let productsPrice: MultProductsResponse[];
      if (
        magentoProducts &&
        magentoProducts.every((mp) => skuList.includes(mp.sku))
      ) {
        productsPrice = magentoProducts;
      } else {
        productsPrice = await getMultProducts(
          skuList,
          ECodeType.SKU,
          emitterId,
          skuList.length,
          1,
          customerData.division
        );
      }
      if (productsPrice) {
        setMagentoProducts(productsPrice);

        const filterOnlyFoundProducts = ({ found }: ILongSteelOrderItem) =>
          found;

        const filteredPrices = searchedItems
          .filter(filterOnlyFoundProducts)
          .map((i) => {
            const magProduct = productsPrice?.find((mp) => mp.sku === i.sku);
            const price =
              magProduct?.price?.isAgreementPrice === 'X'
                ? String(magProduct?.price?.salesAgreementData?.salesAgreement)
                : magProduct?.price?.creditPrice;
            const gpmDescription = magProduct?.custom_attributes.find(
              (v) => v.attribute_code === 'descricao_gpm'
            );
            return {
              ...i,
              code: String(magProduct?.id),
              description: gpmDescription?.value,
              price,
              hasAgreementPrice:
                magProduct &&
                magProduct?.price &&
                !!magProduct?.price?.isAgreementPrice
            };
          });

        dispatch(
          sendTagging({
            event: 'view_item_list', // alterar para apenas visão do usuário
            ecommerce: {
              screenName: 'sua_planilha',
              tipo_compra: 'planilha',
              paginacao: '',
              items: filteredPrices.map(
                (item) =>
                  ({
                    screenName: 'sua_planilha',
                    tipo_compra: 'planilha',
                    item_id: item.sku ?? '',
                    item_name: item.description,
                    price: item.price,
                    unidade_de_medida: item.measureUnit,
                    quantity: item.quantity?.toString() ?? 1,
                    item_category: item.description ?? '',
                    cod_gerdau: item.sku,
                    preco_acordado: filteredPrices.find(
                      (filteredProduct) => filteredProduct.sku === item.sku
                    )?.hasAgreementPrice
                  } as IProductCardTagging)
              )
            }
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterPricesAndsearchedItems = () => {
    const filterOnlyFoundProducts = ({ found }: ILongSteelOrderItem) => found;
    return searchedItems.filter(filterOnlyFoundProducts).map((i) => {
      const magProduct = magentoProducts?.find((mp) => mp.sku === i.sku);
      const price =
        magProduct?.price?.isAgreementPrice === 'X'
          ? String(magProduct?.price?.salesAgreementData?.salesAgreement)
          : magProduct?.price?.creditPrice;
      const gpmDescription = magProduct?.custom_attributes.find(
        (v) => v.attribute_code === 'descricao_gpm'
      );
      return {
        ...i,
        code: String(magProduct?.id),
        description: gpmDescription?.value,
        price,
        hasAgreementPrice:
          magProduct && magProduct.price && !!magProduct.price.isAgreementPrice
      };
    });
  };

  //get cart
  useEffect(() => {
    cart.getCartIdOrGenerateOne().catch(console.error);
  }, []);

  // precisa melhorar esse tageamento de falha de itens nao encontrados TAGUEAMENTO NAO UTILIZADO
  useEffect(() => {
    const notFoundItems = searchedItems.filter((item) => !item.found);
    const itemsNotSentFromLocalStorage = localStorage.getItem('itemsNotSent');
    if (notFoundItems.length) {
      createTagsForProductsNotFound(notFoundItems);
    }
    if (
      Array.isArray(itemsNotSentFromLocalStorage) &&
      itemsNotSentFromLocalStorage?.length
    ) {
      dispatch(
        sendTagging({
          event: 'limite_produtos_excedido',
          screenName: 'enviar_planilha',
          etapa: 'planilha_carregada'
        })
      );
    }
  }, [searchedItems]);

  useEffect(() => {
    if (!customerLoading && searchedItems.length) {
      processPricesAndSendTag();
    }
  }, [searchedItems]);

  useEffect(() => {
    setIsContinueDisabled(
      searchedItems.length === 0 ||
        searchedItems.every((i) => i.found === false)
    );
  }, [searchedItems]);

  const uploadDisabled =
    !emitterId ||
    orderItemSearch?.searchingItems ||
    !!customersState?.loadingBlocks ||
    !!customersState?.loadingProductDescription;

  const canShowSearchedItems = (): boolean => {
    return (
      (pathname.includes(EOrderSettingsRoutes.SEARCH_ITEMS) ||
        pathname.includes(EOrderSettingsRoutes.SPREADSHEET_UPLOAD)) &&
      orderItemSearch.searchedItems.length > 0
    );
  };

  if (!customerData || isBlockedCustomer) {
    return (
      <MessageChooseItem>
        {openBlockedModal && (
          <BlockedModal onCloseModal={onCloseBlockedModal} />
        )}
        <MessageWrapperChooseItem>
          <Message>Selecione emissor e recebedor</Message>
          <Message> para continuar.</Message>
        </MessageWrapperChooseItem>
      </MessageChooseItem>
    );
  }

  return (
    <>
      <LoadingItems
        enabled={isLoading || orderItemSearch?.searchingItems}
        filename={filename}
        text={isLoading ? 'Processando os itens...' : undefined}
      />
      <ModalRemoveAllSearchedItems />
      <Container>
        <Header>
          <Title level={2}>Envie sua planilha</Title>
          <span>
            Crie sua planilha contendo os dados dos produtos e envie para montar
            o seu carrinho através dela.
          </span>
        </Header>
        <ContentButton>
          <a href="/examples/Planilha_padrao_de_pedido.xlsx">
            <Button
              name="templateSpreadsheetModalButton"
              variant="secondary"
              onClick={() => {
                createSpreadsheetSubmissionTags('baixar_modelo_planilha');
              }}
              loading={isLoading}
              disabled={isLoading}
            >
              Baixar modelo de planilha
            </Button>
          </a>
          <Button
            name="configModalButton"
            variant="secondary"
            onClick={() => {
              setCanShowConfigModal(true);
            }}
            loading={isLoading}
            disabled={isLoading}
          >
            Configurar envio da planilha
          </Button>
        </ContentButton>
        {orderItemSearch.searchedItems.length <= 0 && (
          <Wrapper>
            <UploadFile
              acceptedFiles={{
                'application/xls': ['.xls'],
                'application/xlsx': ['.xlsx']
              }}
              maxSizeMB={5}
              buttonLabel="Enviar planilha"
              onChangeFiles={(files) => {
                onFileUploaded(files[0] as RcFile);
                createSpreadsheetSubmissionTags('upload_planilha');
              }}
              disabled={uploadDisabled}
              hideChips
            />
          </Wrapper>
        )}
      </Container>
      {canShowSearchedItems() && (
        <SearchedListMvp
          searchedItems={orderItemSearch.searchedItems}
          searchingItemsSpreadsheetItemsExceeded={
            orderItemSearch.searchingItemsSpreadsheetItemsExceeded
          }
          foundItens={
            orderItemSearch.searchedItems.filter((item) => item.found).length
          }
          notFoundItens={
            orderItemSearch.searchedItems.filter((item) => !item.found).length
          }
          onRemoveAllItems={removeAllSearchedItem}
          onRemoveSearchedItem={removeSearchedItem}
          onAddSearchedItem={(items: ILongSteelOrderItem[]) =>
            addSearchedItem(items)
          }
          userOrderEntryPreference={userOrderEntryPreference}
          emitterId={emitterId}
        />
      )}

      <ModalProductNotFound />

      <SpreadsheetConfigModal
        canShowModal={canShowConfigModal && !isLoading}
        setCanShowModal={setCanShowConfigModal}
        orderEntryPreference={userOrderEntryPreference}
        onConfirmClick={onConfirmSpreadsheetConfig}
      />

      {!canShowConfigModal && (
        <ShoppingCartBottomBar
          isContinueDisabled={isContinueDisabled}
          onGoToCheckout={() =>
            goToNextStep(
              orderItemSearch.searchedItems.filter((item) => !item.found)
                .length > 0
            )
          }
          showQuantity={false}
          loading={isLoading}
        />
      )}
    </>
  );
};

export default SpreadsheetUploadMvp;
